import React, {useEffect, useState, useCallback} from 'react';
import {View, ScrollView, StyleSheet} from 'react-native';
import {TextInput, Button, Snackbar, Text} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation} from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useConfigStore} from '../../store/useConfigStore';
import GradientHeader from '../../components/common/GradientHeader';
import {colors, spacing} from '../../theme';

const BillSettingsScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const {fetchByType, getProperty, updateProperties, isLoading} = useConfigStore();

  const [termsHeader, setTermsHeader] = useState('');
  const [footer, setFooter] = useState('');
  const [termsContent, setTermsContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  useEffect(() => {
    fetchByType('BILL');
  }, [fetchByType]);

  useEffect(() => {
    setTermsHeader(getProperty('BILL', 'BILL_TERMS_HEADER'));
    setFooter(getProperty('BILL', 'BILL_FOOTER'));
    setTermsContent(getProperty('BILL', 'BILL_TERMS'));
  }, [getProperty, useConfigStore.getState().properties]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await updateProperties('BILL', [
        {code: 'BILL_TERMS_HEADER', name: 'Terms & Conditions Header', value: termsHeader},
        {code: 'BILL_FOOTER', name: 'Bill Footer', value: footer},
        {code: 'BILL_TERMS', name: 'Terms & Conditions', value: termsContent},
      ]);
      setSnackbar(t('bill.settingsSaved'));
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  }, [termsHeader, footer, termsContent, updateProperties, t]);

  return (
    <View style={styles.container}>
      <GradientHeader title={t('bill.settingsTitle')} onBack={() => navigation.goBack()} />

      <ScrollView contentContainerStyle={styles.scrollContent}>
        {/* Terms & Conditions */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Icon name="file-document-outline" size={18} color={colors.primary} />
            <Text style={styles.sectionTitle}>Terms & Conditions</Text>
          </View>
          <Text style={styles.sectionHint}>Displayed at the bottom of bills and invoices</Text>

          <TextInput
            label={t('bill.termsHeader')}
            value={termsHeader}
            onChangeText={setTermsHeader}
            mode="outlined"
            style={styles.input}
            outlineColor={colors.border}
            activeOutlineColor={colors.primary}
          />

          <TextInput
            label={t('bill.termsContent')}
            value={termsContent}
            onChangeText={setTermsContent}
            mode="outlined"
            multiline
            numberOfLines={6}
            style={styles.input}
            outlineColor={colors.border}
            activeOutlineColor={colors.primary}
          />
        </View>

        {/* Footer */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Icon name="page-layout-footer" size={18} color={colors.primary} />
            <Text style={styles.sectionTitle}>Bill Footer</Text>
          </View>
          <Text style={styles.sectionHint}>Custom text shown at the very bottom of the bill</Text>

          <TextInput
            label={t('bill.footer')}
            value={footer}
            onChangeText={setFooter}
            mode="outlined"
            style={styles.input}
            outlineColor={colors.border}
            activeOutlineColor={colors.primary}
          />
        </View>

        <Button
          mode="contained"
          onPress={handleSave}
          loading={saving}
          disabled={saving || isLoading}
          style={styles.saveBtn}
          buttonColor={colors.primary}>
          {t('common.save')}
        </Button>
      </ScrollView>

      <Snackbar visible={!!snackbar} onDismiss={() => setSnackbar('')} duration={2000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  scrollContent: {paddingBottom: spacing.xl},

  section: {
    backgroundColor: colors.surface,
    marginBottom: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 2,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.text,
  },
  sectionHint: {
    fontSize: 12,
    color: colors.textSecondary,
    marginBottom: spacing.md,
    marginLeft: 24,
  },
  input: {marginBottom: spacing.sm, backgroundColor: colors.surface},
  saveBtn: {marginHorizontal: spacing.md, marginTop: spacing.sm, borderRadius: 8},
});

export default BillSettingsScreen;
